import { useLayoutEffect, useRef } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { recallRailOffset, rememberRailOffset } from '../../application/plants/browseState';
import PlantCard from './PlantCard';
import { CATEGORY_ICONS } from './CategoryIcons';

/**
 * One category, as a shelf (SPEC-011 D-1, SPEC-012 D-1).
 *
 * The rail is a plain overflowing list, not a carousel: no controls over the
 * artwork, no auto-advance, and the scrolling is the platform's own with its
 * momentum intact. What makes it read as a shelf rather than as a clipped row is
 * the card the sheet leaves half past the screen edge — see `catalogue.css`.
 *
 * The heading is a heading and nothing else. A shelf held a `View all` that
 * collapsed the page to one category as a grid; SPEC-012 removes it, so the only
 * thing a category can do is be pushed sideways.
 */
export default function CategoryRail({ rail, isSaved, onToggleSave }) {
    const { t } = useLocale();
    const trackRef = useRef(null);
    const CategoryIcon = CATEGORY_ICONS[rail.key];
    const categoryName = t(rail.messageKey);

    // Each shelf keeps its own place across a filter change and across a round
    // trip to a plant's page (SPEC-011 D-8). A layout effect, so the offset is
    // applied before the first paint rather than as a visible jump after it.
    useLayoutEffect(() => {
        trackRef.current.scrollLeft = recallRailOffset(rail.key);
    }, [rail.key]);

    return (
        <section className="plant-rail" data-category={rail.key}>
            <div className="plant-rail__head">
                {CategoryIcon ? <CategoryIcon size={26} /> : null}
                <h2 className="plant-rail__title">{categoryName}</h2>
            </div>
            <ul
                className="plant-rail__track"
                ref={trackRef}
                onScroll={(event) => rememberRailOffset(rail.key, event.currentTarget.scrollLeft)}
                aria-label={t('plants.rail.label', { category: categoryName })}
            >
                {rail.plants.map((plant) => (
                    <li className="plant-rail__item" key={plant.id}>
                        <PlantCard
                            plant={plant}
                            isSaved={isSaved(plant.id)}
                            onToggleSave={onToggleSave}
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
}
